import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  Users, 
  GraduationCap, 
  ChevronDown, 
  Wand2, 
  FileSearch, 
  Clock, 
  ListChecks, 
  ShieldCheck, 
  ArrowRight,
  Bell,
  Upload,
  CheckCircle2,
  AlertCircle,
  ArrowLeft,
  Home as HomeIcon,
  Folder,
  MessageSquare,
  User as UserIcon,
  LayoutDashboard,
  FileText,
  Plane,
  Briefcase,
  CheckSquare,
  BarChart2,
  Settings,
  Check,
  Lock,
  Apple,
  Smartphone,
  Menu,
  X,
  Download,
  FileOutput,
  BrainCircuit,
  FileCheck
} from 'lucide-react';
import Button from '../../components/common/Button';
import './styles.scss';

export default function Home() {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <div className="home-container">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar__left">
          <div className="navbar__logo">
            <img src="/campusready_logo.svg" alt="CampusReady Logo" className="logo-icon" />
            <span className="logo-text">CampusReady.ai</span>
          </div>
        </div>
        
        <div className="navbar__center">
          <a href="#campus-staff" className="nav-link">For Campus Staff</a>
          <a href="#agents" className="nav-link">For Agents</a>
          <a href="#students" className="nav-link">For Students</a>
          <a href="#about" className="nav-link">About Us</a>
        </div>

        <div className="navbar__right">
          <Button 
            variant="outline" 
            icon={<Building2 size={16} />} 
            onClick={() => navigate('/login')}
            className="login-btn"
          >
            University Login
          </Button>
          <Button 
            variant="primary" 
            onClick={() => navigate('/register')}
            className="signup-btn"
          >
            Get Started
          </Button>
        </div>

        <button className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Mobile Menu */}
        <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
          <a href="#campus-staff" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>For Campus Staff</a>
          <a href="#agents" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>For Agents</a>
          <a href="#students" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>For Students</a>
          <a href="#about" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>About Us</a>
          <div className="mobile-menu-actions">
            <Button variant="outline" onClick={() => navigate('/login')} className="mobile-btn">
              University Login
            </Button>
            <Button variant="primary" onClick={() => navigate('/register')} className="mobile-btn">
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero__content">
          <div className="badge">
            <Wand2 size={14} className="badge-icon" />
            <span>AI-Powered International Enrollment</span>
          </div>
          
          <h1 className="hero-title">
            Turn international<br/>
            applicants into<br/>
            <span className="text-primary">campus-ready</span><br/>
            students.
          </h1>
          
          <p className="hero-subtitle">
            CampusReady.ai simplifies international admissions and<br/>
            visa readiness with AI-powered document review, real-time<br/>
            updates, and clear next steps.
          </p>

          <div className="hero-features-row">
            <div className="mini-feature">
              <div className="icon-box"><FileSearch size={18} /></div>
              <span>AI Document<br/>Review</span>
            </div>
            <div className="mini-feature">
              <div className="icon-box"><Clock size={18} /></div>
              <span>Real-time<br/>Updates</span>
            </div>
            <div className="mini-feature">
              <div className="icon-box"><ListChecks size={18} /></div>
              <span>Personalized<br/>Checklists</span>
            </div>
            <div className="mini-feature">
              <div className="icon-box"><ShieldCheck size={18} /></div>
              <span>Visa & I-20<br/>Readiness</span>
            </div>
          </div>

          <div className="hero-trust-badge">
            <Lock size={14} color="#16A34A" />
            <span>100% FERPA Compliant &amp; End-to-End Encrypted</span>
          </div>
        </div>

        {/* CSS Mockup Graphics Area */}
        <div className="hero__visuals">
          <div className="mockup-canvas">
            
            {/* Left Background Card: Upload Document */}
            <div className="upload-card floating-slow" style={{animationDelay: '-2s'}}>
              <div className="upload-card__topbar">
                <ArrowLeft size={14} color="#64748B" />
                <span>Upload Document</span>
              </div>
              <div className="upload-card__content">
                <h5>Financial Statement</h5>
                <p>Upload a clear statement showing available funds.</p>
                <div className="drop-zone">
                  <div className="drop-icon"><Upload size={22} color="#2563EB" /></div>
                  <p>Tap to upload or <strong>browse</strong></p>
                  <span>PDF, JPG, PNG up to 10MB</span>
                </div>
                <div className="recent-uploads">
                  <h6>Recent Uploads</h6>
                  <div className="upload-item">
                    <div className="file-icon pdf">PDF</div>
                    <div className="file-info">
                      <strong>Bank Statement.pdf</strong>
                      <span>May 12, 2025 · Under Review</span>
                    </div>
                  </div>
                  <div className="upload-item">
                    <div className="file-icon pdf">PDF</div>
                    <div className="file-info">
                      <strong>Scholarship Award.pdf</strong>
                      <span>May 10, 2025 · Uploaded</span>
                    </div>
                  </div>
                  <div className="upload-item">
                    <div className="file-icon img">JPG</div>
                    <div className="file-info">
                      <strong>Passport_Scan.jpg</strong>
                      <span>May 8, 2025 · Verified</span>
                    </div>
                  </div>
                  <div className="upload-item">
                    <div className="file-icon doc">DOC</div>
                    <div className="file-info">
                      <strong>Sponsor_Letter.docx</strong>
                      <span>May 5, 2025 · Needs Attention</span>
                    </div>
                  </div>
                  <div className="upload-item">
                    <div className="file-icon pdf">PDF</div>
                    <div className="file-info">
                      <strong>Parent Employment Letter.pdf</strong>
                      <span>May 8, 2025 · Uploaded</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Background Card: Dashboard */}
            <div className="dashboard-card floating-slow">
              {/* Mockup Sidebar */}
              <div className="dash-sidebar">
                <div className="dash-sidebar-logo">
                  <Wand2 size={12} color="#fff" />
                  <span>CampusReady.ai</span>
                </div>
                <div className="dash-sidebar-nav">
                  <div className="dash-nav-item active"><LayoutDashboard size={10} /> Dashboard</div>
                  <div className="dash-nav-item"><FileText size={10} /> Applications</div>
                  <div className="dash-nav-item"><Users size={10} /> Applicants</div>
                  <div className="dash-nav-item"><Folder size={10} /> Documents</div>
                  <div className="dash-nav-item"><Plane size={10} /> I-20 & Visa</div>
                  <div className="dash-nav-item"><Briefcase size={10} /> Agents</div>
                  <div className="dash-nav-item"><MessageSquare size={10} /> Communication</div>
                  <div className="dash-nav-item"><CheckSquare size={10} /> Tasks & Reminders</div>
                  <div className="dash-nav-item"><BarChart2 size={10} /> Reports & Analytics</div>
                </div>
              </div>

              {/* Main content wrapper */}
              <div className="dash-main">

              {/* Top header bar */}
              <div className="dash-topbar">
                <div>
                  <h3>Dashboard</h3>
                  <p>Overview of international admissions</p>
                </div>
                <div className="dash-topbar-icons">
                  <Bell size={15} color="#64748B" />
                  <div className="dash-avatar">
                    <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="18" cy="18" r="18" fill="#DBEAFE"/>
                      <circle cx="18" cy="14" r="6" fill="#60A5FA"/>
                      <ellipse cx="18" cy="30" rx="12" ry="9" fill="#60A5FA"/>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Mini stat cards row */}
              <div className="dash-mini-stats">
                <div className="mini-stat blue">
                  <span className="mini-val">1,248</span>
                  <span className="mini-lbl">Applications</span>
                </div>
                <div className="mini-stat amber">
                  <span className="mini-val">352</span>
                  <span className="mini-lbl">Under Review</span>
                </div>
                <div className="mini-stat green">
                  <span className="mini-val">186</span>
                  <span className="mini-lbl">Admitted</span>
                </div>
                <div className="mini-stat purple">
                  <span className="mini-val">142</span>
                  <span className="mini-lbl">I-20 Ready</span>
                </div>
                <div className="mini-stat teal">
                  <span className="mini-val">98</span>
                  <span className="mini-lbl">Enrolled</span>
                </div>
              </div>

              {/* Application Funnel Chart */}
              <div className="dash-content-grid">
                <div className="dash-funnel">
                  <h4>Application Funnel</h4>
                  <div className="funnel-row">
                    <span className="funnel-lbl">Inquiries</span>
                    <div className="funnel-bar-wrapper"><div className="funnel-bar" style={{width: '100%'}}></div></div>
                    <span className="funnel-val">2,487</span>
                  </div>
                  <div className="funnel-row">
                    <span className="funnel-lbl">Started</span>
                    <div className="funnel-bar-wrapper"><div className="funnel-bar" style={{width: '50%'}}></div></div>
                    <span className="funnel-val">1,248</span>
                  </div>
                  <div className="funnel-row">
                    <span className="funnel-lbl">Submitted</span>
                    <div className="funnel-bar-wrapper"><div className="funnel-bar" style={{width: '36%'}}></div></div>
                    <span className="funnel-val">892</span>
                  </div>
                  <div className="funnel-row">
                    <span className="funnel-lbl">Under Review</span>
                    <div className="funnel-bar-wrapper"><div className="funnel-bar" style={{width: '14%'}}></div></div>
                    <span className="funnel-val">352</span>
                  </div>
                  <div className="funnel-row">
                    <span className="funnel-lbl">Admitted</span>
                    <div className="funnel-bar-wrapper"><div className="funnel-bar" style={{width: '7%'}}></div></div>
                    <span className="funnel-val">186</span>
                  </div>
                  <div className="funnel-row">
                    <span className="funnel-lbl">Enrolled</span>
                    <div className="funnel-bar-wrapper"><div className="funnel-bar" style={{width: '4%'}}></div></div>
                    <span className="funnel-val">98</span>
                  </div>
                </div>

                <div className="dash-recent-activity">
                  <h4>Recent Activity</h4>
                  <div className="activity-item">
                    <div className="activity-dot blue"></div>
                    <div className="activity-text">
                      <strong>Ananya Kumar's financial document was verified</strong>
                      <span>2 minutes ago</span>
                    </div>
                  </div>
                  <div className="activity-item">
                    <div className="activity-dot green"></div>
                    <div className="activity-text">
                      <strong>Mohammad Rahman uploaded passport</strong>
                      <span>15 minutes ago</span>
                    </div>
                  </div>
                  <div className="activity-item">
                    <div className="activity-dot purple"></div>
                    <div className="activity-text">
                      <strong>I-20 issued for Priya Patel</strong>
                      <span>1 hour ago</span>
                    </div>
                  </div>
                  <div className="activity-item">
                    <div className="activity-dot blue"></div>
                    <div className="activity-text">
                      <strong>New application from John Doe</strong>
                      <span>2 hours ago</span>
                    </div>
                  </div>
                </div>

                <div className="dash-action-items">
                  <h4>Pending Actions</h4>
                  <div className="action-row">
                    <span className="dtag red">URGENT</span>
                    <span className="action-text">Review 12 pending financial documents</span>
                  </div>
                  <div className="action-row">
                    <span className="dtag orange">TODO</span>
                    <span className="action-text">Approve I-20 issuance for 5 students</span>
                  </div>
                  <div className="action-row">
                    <span className="dtag blue">REVIEW</span>
                    <span className="action-text">Check new passport uploads</span>
                  </div>
                  <div className="action-row">
                    <span className="dtag green">DONE</span>
                    <span className="action-text">Weekly admissions report generated</span>
                  </div>
                </div>
              </div>

              </div> {/* End dash-main */}
            </div>

            {/* Mobile Phone Mockup */}
            <div className="phone-mockup floating">
              {/* Dynamic Island / Notch */}
              <div className="phone-notch"></div>
              
              {/* Status bar */}
              <div className="phone-statusbar">
                <span className="time">12:46</span>
                <div className="status-icons">
                  <div className="status-dots">
                    <div className="s-dot"></div>
                    <div className="s-dot"></div>
                    <div className="s-dot"></div>
                    <div className="s-dot"></div>
                  </div>
                  <div className="wifi-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{width: '14px', height: '14px'}}><path d="M5 12.55a11 11 0 0 1 14.08 0"></path><path d="M1.42 9a16 16 0 0 1 21.16 0"></path><path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path><line x1="12" y1="20" x2="12.01" y2="20"></line></svg>
                  </div>
                  <div className="battery-icon">
                    <div className="battery-level"></div>
                  </div>
                </div>
              </div>

              {/* Mockup Screen Content */}
              <div className="phone-screen-content">
                
                {/* Top App Header */}
                <div className="app-header-bar">
                  <h3 className="app-title">CampusReady</h3>
                  <div className="uom-badge">UOM</div>
                </div>

                <div className="header-divider"></div>

                {/* Welcome Header */}
                <div className="welcome-header">
                  <div className="welcome-text">
                    <h2>Hi, Mehmet 👋</h2>
                    <p>University of Memphis • Fall 2026 • Intensive English for Internationals (IEI)</p>
                  </div>
                  <div className="graduation-icon">
                    <GraduationCap size={24} color="#1D4ED8" strokeWidth={2.5} />
                    <div className="sparkle">✨</div>
                  </div>
                </div>

                {/* Progress Card */}
                <div className="progress-card">
                  <span className="prog-title">Application Progress</span>
                  <h1 className="prog-percent">43%</h1>
                  <p className="prog-subtitle">Reviewing requirements</p>
                  
                  <div className="prog-bar-container">
                    <div className="prog-bar-fill" style={{width: '43%'}}></div>
                  </div>

                  <div className="prog-details">
                    <div className="prog-detail-row">
                      <FileText size={10} color="#DBEAFE" />
                      <span><strong>ID: APP-FD076A0C</strong></span>
                    </div>
                    <div className="prog-detail-row">
                      <GraduationCap size={10} color="#DBEAFE" />
                      <span><strong>University of Memphis</strong></span>
                    </div>
                    <div className="prog-detail-row">
                      <FileText size={10} color="#DBEAFE" />
                      <span>Intensive English for Internationals (IEI)</span>
                    </div>
                    <div className="prog-detail-row">
                      <Clock size={10} color="#DBEAFE" />
                      <span>Fall 2026</span>
                    </div>
                    <div className="prog-detail-row orange-text">
                      <Clock size={10} color="#FB923C" />
                      <span style={{color: '#FB923C'}}>Docs Due: 10/11/2027</span>
                    </div>
                  </div>
                </div>

                {/* Next Step Card */}
                <div className="next-step-card">
                  <div className="ns-left">
                    <span className="ns-label">Next Step</span>
                    <h4 className="ns-title">English Proficiency</h4>
                    <span className="ns-sub">Action required</span>
                  </div>
                  <div className="ns-right">
                    <div className="arrow-btn">
                      <ArrowRight size={14} color="#2563EB" />
                    </div>
                  </div>
                </div>

                {/* My Checklist */}
                <div className="checklist-section">
                  <div className="cl-header">
                    <h3>My Checklist</h3>
                    <span>3 of 7 required completed</span>
                  </div>

                  <div className="cl-item">
                    <div className="cl-icon user-icon">
                      <UserIcon size={12} color="#2563EB" />
                    </div>
                    <div className="cl-text">
                      <h4>Complete Profile</h4>
                      <span className="green-text">Completed</span>
                    </div>
                    <div className="cl-status">
                      <div className="status-circle green">
                        <Check size={10} color="#fff" strokeWidth={3} />
                      </div>
                    </div>
                  </div>

                  <div className="cl-item">
                    <div className="cl-icon doc-icon">
                      <FileText size={12} color="#2563EB" />
                    </div>
                    <div className="cl-text">
                      <h4>Passport</h4>
                      <span className="blue-text">Under Review</span>
                    </div>
                    <div className="cl-status">
                      <div className="status-circle blue">
                        <Clock size={10} color="#fff" strokeWidth={2.5} />
                      </div>
                    </div>
                  </div>

                  <div className="cl-item">
                    <div className="cl-icon doc-icon" style={{background: '#FFFBEB'}}>
                      <AlertCircle size={12} color="#F59E0B" />
                    </div>
                    <div className="cl-text">
                      <h4>English Proficiency</h4>
                      <span style={{color: '#F59E0B'}}>Action Required</span>
                    </div>
                    <div className="cl-status">
                      <div className="status-circle" style={{background: '#F59E0B'}}>
                        <AlertCircle size={10} color="#fff" strokeWidth={2.5} />
                      </div>
                    </div>
                  </div>

                  <div className="cl-item" style={{opacity: 0.6}}>
                    <div className="cl-icon doc-icon" style={{background: '#F1F5F9'}}>
                      <FileText size={12} color="#94A3B8" />
                    </div>
                    <div className="cl-text">
                      <h4 style={{color: '#64748B'}}>Financial Docs</h4>
                      <span style={{color: '#94A3B8'}}>Pending</span>
                    </div>
                    <div className="cl-status">
                      <div className="status-circle" style={{background: '#CBD5E1'}}>
                        <Lock size={10} color="#fff" strokeWidth={2.5} />
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              {/* Bottom Nav */}
              <div className="mockup-bottom-nav">
                <div className="nav-item active">
                  <HomeIcon size={18} color="#2563EB" strokeWidth={2} />
                  <span>Home</span>
                </div>
                <div className="nav-item">
                  <div className="icon-wrapper">
                    <MessageSquare size={18} color="#94A3B8" strokeWidth={2} />
                    <div className="nav-badge">2</div>
                  </div>
                  <span>Messages</span>
                </div>
                <div className="nav-item">
                  <Plane size={18} color="#94A3B8" strokeWidth={2} />
                  <span>I-20 & Visa</span>
                </div>
                <div className="nav-item">
                  <UserIcon size={18} color="#94A3B8" strokeWidth={2} />
                  <span>Profile</span>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section id="journey" className="home-section alt-bg">
        <div className="section-container">
          <div className="section-header">
            <h2>The Student Journey</h2>
            <p>From the first click to their first day of class, CampusReady guides students through every milestone of the international enrollment process.</p>
          </div>
          
          <div className="journey-timeline">
            <div className="journey-node">
              <div className="journey-icon-box">
                <FileText size={28} />
              </div>
              <div className="journey-content">
                <h4>1. Apply & Connect</h4>
                <p>Student or agent submits the application and connects to the university portal.</p>
              </div>
            </div>

            <div className="journey-connector"></div>

            <div className="journey-node">
              <div className="journey-icon-box">
                <Wand2 size={28} />
              </div>
              <div className="journey-content">
                <h4>2. Smart Verification</h4>
                <p>AI instantly verifies passports, financial docs, and transcripts with high accuracy.</p>
              </div>
            </div>

            <div className="journey-connector"></div>

            <div className="journey-node">
              <div className="journey-icon-box">
                <CheckCircle2 size={28} />
              </div>
              <div className="journey-content">
                <h4>3. Admission Decision</h4>
                <p>Admissions team approves the verified profile with a single click.</p>
              </div>
            </div>

            <div className="journey-connector"></div>

            <div className="journey-node">
              <div className="journey-icon-box">
                <Plane size={28} />
              </div>
              <div className="journey-content">
                <h4>4. I-20 & Arrival</h4>
                <p>Automated I-20 generation and visa preparation brings the student to campus.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="ai-workflows" className="home-section">
        <div className="section-container">
          <div className="section-header">
            <h2>AI-Driven Intelligent Workflows</h2>
            <p>From complex business logic to document verification, our AI engines adapt to your institution's specific needs, automating the tedious parts of international admissions.</p>
          </div>

          <div className="ai-workflow-grid">
            <div className="ai-wf-card">
              <div className="ai-wf-icon blue"><BrainCircuit size={28} /></div>
              <h4>Custom Business Logic</h4>
              <p>Set custom rules for GPA conversions, financial requirements, and program-specific prerequisites. The AI learns your handbook and applies it instantly.</p>
            </div>
            <div className="ai-wf-card">
              <div className="ai-wf-icon green"><FileCheck size={28} /></div>
              <h4>Smart Document Decisions</h4>
              <p>Passports, bank letters, and transcripts are analyzed automatically. The AI flags discrepancies and confidently approves valid documents.</p>
            </div>
            <div className="ai-wf-card">
              <div className="ai-wf-icon purple"><Wand2 size={28} /></div>
              <h4>Tailored to Your Needs</h4>
              <p>No two universities are identical. Easily tweak AI thresholds and decision criteria to perfectly align with your international enrollment strategy.</p>
            </div>
          </div>

          {/* AI Workflow Diagram */}
          <div className="ai-diagram-container">
            <div className="ai-diagram">
              
              {/* Input Node */}
              <div className="aid-node aid-input">
                <div className="aid-icon"><Upload size={20} /></div>
                <div className="aid-text">
                  <span>Student Uploads</span>
                  <strong>Financial Statement</strong>
                </div>
              </div>

              {/* Connecting Line 1 */}
              <div className="aid-line">
                <div className="aid-dot animated"></div>
              </div>

              {/* Central AI Engine Node */}
              <div className="aid-node aid-engine">
                <div className="ai-pulse-ring"></div>
                <div className="ai-pulse-ring delay"></div>
                <div className="aid-icon"><BrainCircuit size={32} /></div>
                <div className="aid-text">
                  <strong>CampusReady AI Engine</strong>
                </div>
                
                {/* Internal Engine Processes */}
                <div className="aid-processes">
                  <div className="aid-proc"><FileSearch size={12}/> OCR Extraction</div>
                  <div className="aid-proc"><ShieldCheck size={12}/> Fraud Detection</div>
                  <div className="aid-proc"><Settings size={12}/> Business Rules</div>
                </div>
              </div>

              {/* Branching Lines & Outputs */}
              <div className="aid-branches">
                
                {/* Branch 1 (Approved) */}
                <div className="aid-branch">
                  <div className="aid-path-line path-top">
                    <div className="aid-dot animated delay-1"></div>
                  </div>
                  <div className="aid-node aid-result success">
                    <div className="aid-icon"><CheckCircle2 size={18} /></div>
                    <div className="aid-text">
                      <strong>Auto-Approved</strong>
                      <span>Meets $35,000 requirement</span>
                    </div>
                  </div>
                </div>

                {/* Branch 2 (Flagged) */}
                <div className="aid-branch">
                  <div className="aid-path-line path-bottom">
                    <div className="aid-dot animated delay-2"></div>
                  </div>
                  <div className="aid-node aid-result warning">
                    <div className="aid-icon"><AlertCircle size={18} /></div>
                    <div className="aid-text">
                      <strong>Flagged for Review</strong>
                      <span>Missing English translation</span>
                    </div>
                  </div>
                </div>

              </div>

            </div>
          </div>

        </div>
      </section>

      <section id="mobile-app" className="home-section alt-bg mobile-showcase-section">
        <div className="section-container">
          <div className="section-header">
            <h2>Built Mobile-First</h2>
            <p>Students and agents can manage everything from their phone. Real-time updates, document uploads, and instant notifications — all on the go.</p>
          </div>

          <div className="mobile-showcase-layout">

            {/* Left feature list */}
            <div className="mobile-feature-list">
              <div className="mf-item">
                <div className="mf-icon blue"><Bell size={18} /></div>
                <div className="mf-text">
                  <strong>Instant Notifications</strong>
                  <span>Get alerted the moment your document is reviewed or status changes.</span>
                </div>
              </div>
              <div className="mf-item">
                <div className="mf-icon green"><Upload size={18} /></div>
                <div className="mf-text">
                  <strong>One-Tap Uploads</strong>
                  <span>Snap a photo of your passport or transcript and upload it in seconds.</span>
                </div>
              </div>
              <div className="mf-item">
                <div className="mf-icon purple"><ListChecks size={18} /></div>
                <div className="mf-text">
                  <strong>Checklist Tracking</strong>
                  <span>See exactly which documents are pending, approved, or need re-upload.</span>
                </div>
              </div>
              <div className="mf-item">
                <div className="mf-icon amber"><MessageSquare size={18} /></div>
                <div className="mf-text">
                  <strong>In-App Messaging</strong>
                  <span>Message your admissions team directly — no email chains needed.</span>
                </div>
              </div>
            </div>

            {/* Center: Phone Mockup — same structure as hero */}
            <div className="mobile-showcase-phone">
              <div className="phone-mockup" style={{position: 'relative', left: 'auto', top: 'auto', animation: 'float 6s ease-in-out infinite'}}>
                <div className="phone-notch"></div>
                <div className="phone-statusbar">
                  <span className="time">9:41</span>
                  <div className="status-icons">
                    <div className="status-dots">
                      <div className="s-dot"></div>
                      <div className="s-dot"></div>
                      <div className="s-dot"></div>
                      <div className="s-dot"></div>
                    </div>
                    <div className="wifi-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{width: '14px', height: '14px'}}><path d="M5 12.55a11 11 0 0 1 14.08 0"></path><path d="M1.42 9a16 16 0 0 1 21.16 0"></path><path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path><line x1="12" y1="20" x2="12.01" y2="20"></line></svg>
                    </div>
                    <div className="battery-icon">
                      <div className="battery-level"></div>
                    </div>
                  </div>
                </div>

                <div className="phone-screen-content">
                  <div className="app-header-bar">
                    <h3 className="app-title">CampusReady</h3>
                    <div className="uom-badge">UOM</div>
                  </div>
                  <div className="header-divider"></div>

                  <div className="welcome-header">
                    <div className="welcome-text">
                      <h2>Hi, Mehmet 👋</h2>
                      <p>University of Memphis • Fall 2026 • IEI</p>
                    </div>
                    <div className="graduation-icon">
                      <GraduationCap size={24} color="#1D4ED8" strokeWidth={2.5} />
                      <div className="sparkle">✨</div>
                    </div>
                  </div>

                  <div className="progress-card">
                    <span className="prog-title">Application Progress</span>
                    <h1 className="prog-percent">68%</h1>
                    <p className="prog-subtitle">Documents under review</p>
                    <div className="prog-bar-container">
                      <div className="prog-bar-fill" style={{width: '68%'}}></div>
                    </div>
                    <div className="prog-details">
                      <div className="prog-detail-row">
                        <FileText size={10} color="#DBEAFE" />
                        <span><strong>ID: APP-FD076A0C</strong></span>
                      </div>
                      <div className="prog-detail-row">
                        <GraduationCap size={10} color="#DBEAFE" />
                        <span><strong>University of Memphis</strong></span>
                      </div>
                      <div className="prog-detail-row orange-text">
                        <Clock size={10} color="#FB923C" />
                        <span style={{color: '#FB923C'}}>Docs Due: 10/11/2027</span>
                      </div>
                    </div>
                  </div>

                  <div className="next-step-card">
                    <div className="ns-left">
                      <span className="ns-label">Next Step</span>
                      <h4 className="ns-title">English Proficiency</h4>
                      <span className="ns-sub">Action required</span>
                    </div>
                    <div className="arrow-btn"><ArrowRight size={14} color="#2563EB" /></div>
                  </div>

                  <div className="checklist-section">
                    <div className="cl-header">
                      <h3>My Checklist</h3>
                      <span>4 of 6 done</span>
                    </div>
                    <div className="cl-item">
                      <div className="cl-icon"><UserIcon size={12} color="#2563EB" /></div>
                      <div className="cl-text"><h4>Complete Profile</h4><span className="green-text">Completed</span></div>
                      <div className="status-circle green"><Check size={10} color="#fff" strokeWidth={3} /></div>
                    </div>
                    <div className="cl-item">
                      <div className="cl-icon"><FileText size={12} color="#2563EB" /></div>
                      <div className="cl-text"><h4>Passport</h4><span className="green-text">Verified</span></div>
                      <div className="status-circle green"><Check size={10} color="#fff" strokeWidth={3} /></div>
                    </div>
                    <div className="cl-item">
                      <div className="cl-icon" style={{background: '#FFFBEB'}}><AlertCircle size={12} color="#F59E0B" /></div>
                      <div className="cl-text"><h4>English Proficiency</h4><span style={{color: '#F59E0B'}}>Action Required</span></div>
                      <div className="status-circle" style={{background: '#F59E0B'}}><AlertCircle size={10} color="#fff" strokeWidth={2.5} /></div>
                    </div>
                  </div>
                </div>

                <div className="mockup-bottom-nav">
                  <div className="nav-item active"><HomeIcon size={18} color="#2563EB" strokeWidth={2} /><span>Home</span></div>
                  <div className="nav-item">
                    <div className="icon-wrapper"><MessageSquare size={18} color="#94A3B8" strokeWidth={2} /><div className="nav-badge">2</div></div>
                    <span>Messages</span>
                  </div>
                  <div className="nav-item"><Plane size={18} color="#94A3B8" strokeWidth={2} /><span>I-20 &amp; Visa</span></div>
                  <div className="nav-item"><UserIcon size={18} color="#94A3B8" strokeWidth={2} /><span>Profile</span></div>
                </div>
              </div>
            </div>

            {/* Right feature list */}
            <div className="mobile-feature-list right">
              <div className="mf-item">
                <div className="mf-icon teal"><Plane size={18} /></div>
                <div className="mf-text">
                  <strong>Visa Prep Guidance</strong>
                  <span>Step-by-step visa appointment tips and I-20 status updates.</span>
                </div>
              </div>
              <div className="mf-item">
                <div className="mf-icon blue"><LayoutDashboard size={18} /></div>
                <div className="mf-text">
                  <strong>Live Status Dashboard</strong>
                  <span>Track your application from submission to enrollment in real time.</span>
                </div>
              </div>
              <div className="mf-item">
                <div className="mf-icon green"><ShieldCheck size={18} /></div>
                <div className="mf-text">
                  <strong>FERPA Compliant Security</strong>
                  <span>End-to-end encrypted documents. Your students' data is always protected.</span>
                </div>
              </div>
              <div className="mf-item">
                <div className="mf-icon red"><Smartphone size={18} /></div>
                <div className="mf-text">
                  <strong>iOS &amp; Android</strong>
                  <span>Native apps on both platforms with full offline support.</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <section id="reports" className="home-section">

        <div className="section-container">
          <div className="section-header">
            <h2>Reports &amp; Analytics</h2>
            <p>Stop guessing. See exactly where every student stands, where your pipeline is stalling, and what's working — all in one place.</p>
          </div>

          <div className="analytics-stats-grid">
            <div className="analytics-stat-card">
              <span className="stat-number">100%</span>
              <span className="stat-label">Pipeline Visibility</span>
              <p className="stat-desc">From first application to first day on campus. No blind spots.</p>
            </div>
            <div className="analytics-stat-card highlight">
              <span className="stat-number">1-click</span>
              <span className="stat-label">Export Anywhere</span>
              <p className="stat-desc">Generate polished PDF summaries or raw CSV data in seconds.</p>
            </div>
            <div className="analytics-stat-card">
              <span className="stat-number">Real-time</span>
              <span className="stat-label">Live Dashboards</span>
              <p className="stat-desc">Visa rates, I-20 issuance, approval funnels — always up to date.</p>
            </div>
            <div className="analytics-stat-card">
              <span className="stat-number">Any</span>
              <span className="stat-label">Filter Combination</span>
              <p className="stat-desc">Slice by program, intake, date range, or status. Your data, your lens.</p>
            </div>
          </div>

          {/* Charts Panel */}
          <div className="analytics-charts-panel">

            {/* Left: Admissions Funnel Bars */}
            <div className="acp-card acp-funnel">
              <div className="acp-card-header">
                <span>Admissions Pipeline</span>
                <div className="acp-pill">Fall 2026</div>
              </div>
              <div className="acp-funnel-bars">
                <div className="acp-bar-row"><span>Applied</span><div className="acp-bar-track"><div className="acp-bar" style={{width: '100%', background: '#3B82F6'}}></div></div><span className="acp-val">2,487</span></div>
                <div className="acp-bar-row"><span>Submitted</span><div className="acp-bar-track"><div className="acp-bar" style={{width: '72%', background: '#8B5CF6'}}></div></div><span className="acp-val">1,791</span></div>
                <div className="acp-bar-row"><span>Under Review</span><div className="acp-bar-track"><div className="acp-bar" style={{width: '50%', background: '#F59E0B'}}></div></div><span className="acp-val">1,243</span></div>
                <div className="acp-bar-row"><span>Admitted</span><div className="acp-bar-track"><div className="acp-bar" style={{width: '28%', background: '#10B981'}}></div></div><span className="acp-val">695</span></div>
                <div className="acp-bar-row"><span>I-20 Ready</span><div className="acp-bar-track"><div className="acp-bar" style={{width: '18%', background: '#14B8A6'}}></div></div><span className="acp-val">448</span></div>
                <div className="acp-bar-row"><span>Enrolled</span><div className="acp-bar-track"><div className="acp-bar" style={{width: '10%', background: '#22C55E'}}></div></div><span className="acp-val">249</span></div>
              </div>
            </div>

            {/* Right: Donut + Export */}
            <div className="acp-right-col">
              <div className="acp-card acp-donut-card">
                <div className="acp-card-header">
                  <span>Status Breakdown</span>
                </div>
                <div className="acp-donut-body">
                  <svg viewBox="0 0 80 80" className="acp-donut-svg">
                    <circle cx="40" cy="40" r="30" fill="none" stroke="#F1F5F9" strokeWidth="14" />
                    <circle cx="40" cy="40" r="30" fill="none" stroke="#3B82F6" strokeWidth="14" strokeDasharray="67 122" strokeDashoffset="0" />
                    <circle cx="40" cy="40" r="30" fill="none" stroke="#10B981" strokeWidth="14" strokeDasharray="38 122" strokeDashoffset="-67" />
                    <circle cx="40" cy="40" r="30" fill="none" stroke="#F59E0B" strokeWidth="14" strokeDasharray="25 122" strokeDashoffset="-105" />
                    <circle cx="40" cy="40" r="30" fill="none" stroke="#EF4444" strokeWidth="14" strokeDasharray="11 122" strokeDashoffset="-130" />
                    <text x="40" y="37" textAnchor="middle" fill="#0F172A" fontSize="10" fontWeight="800">2.4k</text>
                    <text x="40" y="48" textAnchor="middle" fill="#64748B" fontSize="5.5">Applications</text>
                  </svg>
                  <div className="acp-legend">
                    <div className="acp-legend-item"><span className="dot blue"></span>Under Review</div>
                    <div className="acp-legend-item"><span className="dot green"></span>Admitted</div>
                    <div className="acp-legend-item"><span className="dot amber"></span>In Progress</div>
                    <div className="acp-legend-item"><span className="dot red"></span>Rejected</div>
                  </div>
                </div>
              </div>

              <div className="acp-card acp-export-card">
                <span className="acp-export-label">Export Report</span>
                <div className="acp-export-btns">
                  <div className="acp-export-btn pdf"><FileOutput size={15} /> PDF</div>
                  <div className="acp-export-btn csv"><Download size={15} /> CSV</div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>



      <section id="campus-staff" className="home-section alt-bg">
        <div className="section-container">
          <div className="section-header">
            <h2>For Campus Staff</h2>
            <p>Empower your admissions teams and campus staff to streamline the international enrollment process with AI-driven document verification, intelligent routing, and comprehensive applicant profiles.</p>
          </div>
          <div className="feature-grid">
            <div className="feature-card">
              <div className="icon-box blue"><FileSearch size={24} /></div>
              <h4>Automated Verification</h4>
              <p>Let AI instantly verify passports, transcripts, and English proficiency scores with high accuracy.</p>
            </div>
            <div className="feature-card">
              <div className="icon-box green"><CheckSquare size={24} /></div>
              <h4>AI-Powered Workflows</h4>
              <p>Design custom application requirements tailored to your programs, leveraging AI to streamline review and automate tracking.</p>
            </div>
            <div className="feature-card">
              <div className="icon-box purple"><BarChart2 size={24} /></div>
              <h4>Real-time Analytics</h4>
              <p>Track conversion rates, demographic data, and identify bottlenecks in your admissions pipeline.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="agents" className="home-section">
        <div className="section-container">
          <div className="section-header">
            <h2>For Agents</h2>
            <p>Manage all your students in one place, track their application progress effortlessly, and collaborate directly with university admissions teams.</p>
          </div>
          <div className="feature-grid two-col">
            <div className="feature-card">
              <div className="icon-box orange"><LayoutDashboard size={24} /></div>
              <h4>Centralized Dashboard</h4>
              <p>View all your student applications, pending documents, and university decisions from a single pane of glass.</p>
            </div>
            <div className="feature-card">
              <div className="icon-box teal"><MessageSquare size={24} /></div>
              <h4>Instant Communication</h4>
              <p>Chat directly with university staff to quickly resolve issues and speed up the I-20 issuance process.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="students" className="home-section alt-bg">
        <div className="section-container">
          <div className="section-header">
            <h2>For Students</h2>
            <p>Your journey to studying abroad simplified with smart checklists, real-time updates, and an easy-to-use document upload portal.</p>
            <div className="app-download-badges">
              <span className="download-text">Download the CampusReady app for quick enrollment:</span>
              <div className="badge-row">
                <button className="store-badge apple">
                  <Apple size={24} fill="currentColor" />
                  <div className="store-text">
                    <span className="small">Download on the</span>
                    <span className="big">App Store</span>
                  </div>
                </button>
                <button className="store-badge google">
                  <Smartphone size={24} />
                  <div className="store-text">
                    <span className="small">GET IT ON</span>
                    <span className="big">Google Play</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
          <div className="feature-grid">
            <div className="feature-card">
              <div className="icon-box blue"><ListChecks size={24} /></div>
              <h4>Smart Checklists</h4>
              <p>Personalized document lists based on your specific university and program requirements.</p>
            </div>
            <div className="feature-card">
              <div className="icon-box pink"><Wand2 size={24} /></div>
              <h4>AI-Powered Review</h4>
              <p>AI checks your documents for accuracy and flags issues early before you submit to the university.</p>
            </div>
            <div className="feature-card">
              <div className="icon-box yellow"><Clock size={24} /></div>
              <h4>Stay on Track</h4>
              <p>Real-time push notifications and reminders keep you moving forward towards your enrollment.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="about" className="home-section">
        <div className="section-container">
          <div className="section-header">
            <h2>About Us</h2>
            <p>CampusReady.ai is revolutionizing global education access by bringing powerful AI tools to the international admissions workflow.</p>
          </div>
          <div className="about-content">
            <div className="about-block">
              <h3>Powered by Linqra</h3>
              <p>At the core of CampusReady is <strong>Linqra</strong>, our advanced enterprise AI engine. Linqra seamlessly processes complex international documents, extracting critical data points and validating authenticity with unparalleled precision. By leveraging state-of-the-art vision and language models, Linqra turns days of manual review into an instant, automated workflow.</p>
            </div>
            <div className="about-block">
              <h3>Intelligent Admissions</h3>
              <p>We believe that AI shouldn't just be a buzzword—it should solve real operational bottlenecks. CampusReady's AI agents evaluate everything from financial statements to complex grading scales, providing university staff with clear, actionable insights while giving students immediate feedback on their applications.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
